import { useMemo, useRef, useState } from 'react';
import { BellOff, MessageCircle, Power, Search, SquarePen, Users } from 'lucide-react';
import { getEloLevel } from '../../../engine';
import {
  conversationTitle,
  previewOf,
  timeLabel,
  unreadCount,
  type Conversation,
  type MessagingState,
} from '../../../engine/messaging';
import {
  hasLeftDynamic,
  inboxFor,
  isMessagingDisabled,
  isMuted,
  type ClubRef,
} from '../../../engine/messagingGroups';
import {
  EmptyState,
  MobileSheet,
  SectionTitle,
  Segments,
  initials,
  useSheetIds,
} from '../mobileUi';
import { nameSourceFor } from '../messagingUi';
import { useT } from '../../../i18n';

export interface MobileMessagesProps {
  state: MessagingState;
  member: any;
  effectivePlayerDB: any[];
  clubInfo: any;
  /** The clubs the member belongs to (drives the automatic groups). */
  clubs: ClubRef[];
  onOpen: (conversationId: string) => void;
  onNew: () => void;
  /** Rejoin a club's automatic groups after having left them. */
  onRejoinDynamic?: (clubId: string) => void;
  /** Switch the whole messaging module off / on for this member. */
  onSetDisabled?: (disabled: boolean) => Promise<unknown> | void;
  /** Injectable clock for deterministic renders. */
  now?: number;
}

type Filter = 'all' | 'unread' | 'groups';

// «Meldinger» — the inbox, Messenger-style: tabs Alle · Uleste · Grupper,
// every conversation the member is in (dms, groups, the club, and the
// automatic club groups «Nivå N» / «Alle»), newest activity first.
export function MobileMessages({
  state,
  member,
  effectivePlayerDB,
  clubInfo,
  clubs,
  onOpen,
  onNew,
  onRejoinDynamic,
  onSetDisabled,
  now,
}: MobileMessagesProps) {
  const t = useT();
  const nowMs = now ?? Date.now();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [confirmOff, setConfirmOff] = useState(false);
  const sheetIds = useSheetIds();
  const cancelOff = useRef<HTMLButtonElement>(null);
  const disabled = member ? isMessagingDisabled(state, member.id) : false;
  const names = useMemo(
    () => nameSourceFor(effectivePlayerDB, clubInfo, t),
    [effectivePlayerDB, clubInfo, t],
  );
  const me = member?.id;

  const rows = useMemo(() => {
    if (me == null) return [];
    return inboxFor(state, me, { players: effectivePlayerDB, clubs }).map((c) => ({
      c,
      title: conversationTitle(c, me, names),
      preview: previewOf(state, c, me, names),
      unread: unreadCount(state, c, me),
      muted: isMuted(state, me, c.id),
    }));
  }, [state, me, names, effectivePlayerDB, clubs]);

  const counts = {
    all: rows.length,
    unread: rows.filter((r) => r.unread > 0 && !r.muted).length,
    groups: rows.filter((r) => r.c.kind === 'group' || r.c.kind === 'dynamic').length,
  };

  const visible = useMemo(() => {
    const n = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        // «Alle» lists an automatic group only once it has messages; «Grupper» lists all.
        ((filter === 'all' && (r.c.kind !== 'dynamic' || r.c.lastMessageAt > 0)) ||
          (filter === 'unread' && r.unread > 0 && !r.muted) ||
          (filter === 'groups' && (r.c.kind === 'group' || r.c.kind === 'dynamic'))) &&
        (!n || r.title.toLowerCase().includes(n) || r.preview.toLowerCase().includes(n)),
    );
  }, [rows, filter, q]);

  const leftClubs = me == null ? [] : clubs.filter((c) => hasLeftDynamic(state, me, c.id));

  if (!member) {
    return (
      <div className="px-4 pb-4">
        <SectionTitle>{t('mobile.tab.messages')}</SectionTitle>
        <EmptyState
          title={t('mobile.messages.pickWhoFirst')}
          hint={t('mobile.messages.pickWhoFirstHint')}
        />
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="px-4 pb-4">
        <SectionTitle>{t('mobile.tab.messages')}</SectionTitle>
        <div
          className="rounded-2xl px-4 py-5 text-center"
          style={{ background: '#fff', border: '1px solid #ecefe8' }}
          data-testid="messaging-disabled"
        >
          <div className="flex justify-center mb-2" style={{ color: 'var(--ink-3)' }}>
            <Power size={22} />
          </div>
          <div className="text-[14px] font-semibold text-ink">{t('mobile.messages.offTitle')}</div>
          <p className="text-[12px] text-ink-3 mt-1">{t('mobile.messages.offLead')}</p>
          {onSetDisabled && (
            <button
              onClick={() => void onSetDisabled(false)}
              className="mt-4 rounded-2xl font-semibold text-[14px] px-5 py-3"
              style={{ background: '#c2f02f', color: '#0f1611' }}
            >
              {t('mobile.messages.turnOn')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative px-4 pb-4">
      <SectionTitle
        action={
          <button
            onClick={onNew}
            className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[12px] font-semibold"
            style={{ background: '#0f1611', color: '#fff' }}
            aria-label={t('mobile.newMessage.title')}
          >
            <SquarePen size={13} /> {t('mobile.messages.newShort')}
          </button>
        }
      >
        {t('mobile.tab.messages')}
      </SectionTitle>

      <div
        className="flex items-center gap-2 rounded-xl px-3 py-2 mb-3"
        style={{ background: '#eef0ea' }}
      >
        <Search size={15} className="text-ink-3" />
        <input
          className="flex-1 bg-transparent text-[13px]"
          placeholder={t('mobile.messages.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label={t('mobile.messages.search')}
        />
      </div>

      <Segments<Filter>
        value={filter}
        onChange={setFilter}
        options={[
          { id: 'all', label: t('mobile.messages.filterAll') },
          { id: 'unread', label: t('mobile.messages.filterUnread'), count: counts.unread },
          { id: 'groups', label: t('mobile.messages.filterGroups'), count: counts.groups },
        ]}
      />

      {leftClubs.length > 0 && (
        <div
          className="rounded-2xl px-3 py-2.5 mb-3 flex items-center gap-2 text-[12px]"
          style={{ background: '#fff', border: '1px dashed #e7eae3', color: '#4a544c' }}
        >
          <Users size={14} className="text-ink-3 flex-shrink-0" />
          <span className="flex-1">
            {t('mobile.messages.leftGroups', {
              clubs: leftClubs.map((c) => c.name).join(', '),
            })}
          </span>
          {onRejoinDynamic && (
            <button
              onClick={() => leftClubs.forEach((c) => onRejoinDynamic(c.id))}
              className="font-semibold text-ink underline flex-shrink-0"
            >
              {t('mobile.messages.rejoin')}
            </button>
          )}
        </div>
      )}

      {visible.length === 0 && (
        <EmptyState
          icon={<MessageCircle size={22} />}
          title={
            q
              ? t('mobile.messages.emptySearch')
              : filter === 'unread'
                ? t('mobile.messages.emptyUnread')
                : filter === 'groups'
                  ? t('mobile.messages.emptyGroups')
                  : t('mobile.messages.emptyAll')
          }
          hint={
            q
              ? t('mobile.messages.emptySearchHint')
              : filter === 'unread'
                ? t('mobile.messages.emptyUnreadHint')
                : t('mobile.messages.emptyAllHint')
          }
        />
      )}

      <div className="space-y-1">
        {visible.map(({ c, title, preview, unread, muted }) => (
          <button
            key={c.id}
            onClick={() => onOpen(c.id)}
            className="w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left active:scale-[0.99] transition-transform"
            style={{ background: unread && !muted ? '#fff' : 'transparent' }}
          >
            <ConversationAvatar conversation={c} title={title} />
            <span className="flex-1 min-w-0">
              <span className="flex items-baseline justify-between gap-2">
                <span
                  className={`text-[14px] truncate flex items-center gap-1 ${unread && !muted ? 'font-bold text-ink' : 'font-semibold text-ink'}`}
                >
                  <span className="truncate">{title}</span>
                  {muted && (
                    <BellOff
                      size={11}
                      className="text-ink-3 flex-shrink-0"
                      aria-label={t('mobile.messages.muted')}
                    />
                  )}
                </span>
                <span
                  className="font-mono text-[10px] flex-shrink-0"
                  style={{ color: unread && !muted ? '#0f1611' : 'var(--ink-3)' }}
                >
                  {timeLabel(c.lastMessageAt, nowMs)}
                </span>
              </span>
              <span className="flex items-center justify-between gap-2">
                <span
                  className={`text-[12px] truncate ${unread && !muted ? 'text-ink font-medium' : 'text-ink-3'}`}
                >
                  {c.kind === 'dynamic' && !preview.startsWith('Du:') && c.lastMessageAt === 0
                    ? t('mobile.messages.autoGroupPreview', { n: c.participantIds.length })
                    : preview}
                </span>
                {unread > 0 && !muted && (
                  <span
                    className="font-mono text-[10px] font-bold px-1.5 rounded-full flex-shrink-0"
                    style={{
                      background: '#c2f02f',
                      color: '#0f1611',
                      minWidth: 18,
                      textAlign: 'center',
                    }}
                    aria-label={t('mobile.messages.unreadAria', { count: unread })}
                  >
                    {unread}
                  </span>
                )}
              </span>
            </span>
          </button>
        ))}
      </div>

      {onSetDisabled && (
        <div className="mt-6 text-center">
          <button
            onClick={() => setConfirmOff(true)}
            className="inline-flex items-center gap-1.5 text-[12px] text-ink-3 underline"
          >
            <Power size={12} /> {t('mobile.messages.turnOffForMe')}
          </button>
        </div>
      )}

      {confirmOff && (
        // A CONFIRMATION, so focus opens on «Avbryt» rather than on the action
        // it asks about: an Enter pressed out of habit should not turn
        // messaging off. Named by its question, not by the action's label.
        <MobileSheet
          labelledBy={sheetIds.title}
          describedBy={sheetIds.lead}
          onClose={() => setConfirmOff(false)}
          initialFocus={cancelOff}
          className="pb-5"
        >
          <h2
            id={sheetIds.title}
            className="font-display font-semibold text-ink mt-3"
            style={{ fontSize: 18 }}
          >
            {t('mobile.messages.turnOffConfirmTitle')}
          </h2>
          <p id={sheetIds.lead} className="text-[12px] text-ink-3 mt-1 mb-4">
            {t('mobile.messages.turnOffConfirmLead')}
          </p>
          <button
            onClick={async () => {
              await onSetDisabled?.(true);
              setConfirmOff(false);
            }}
            className="w-full rounded-2xl font-semibold text-[14px] py-3 mb-2"
            style={{ background: '#0f1611', color: '#fff' }}
          >
            {t('mobile.messages.turnOff')}
          </button>
          <button
            ref={cancelOff}
            onClick={() => setConfirmOff(false)}
            className="w-full rounded-2xl font-semibold text-[14px] py-3"
            style={{ background: '#fff', color: '#0f1611', border: '1px solid var(--line-strong)' }}
          >
            {t('mobile.common.cancel')}
          </button>
        </MobileSheet>
      )}
    </div>
  );
}

/** Round avatar: initials for a member, «★» for the club, a group glyph for groups, the level badge for automatic groups. */
export function ConversationAvatar({
  conversation,
  title,
  size = 44,
}: {
  conversation: Conversation;
  title: string;
  size?: number;
}) {
  const base = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.32),
  };
  if (conversation.kind === 'club') {
    return (
      <span
        className="flex items-center justify-center rounded-full flex-shrink-0 font-display font-bold"
        style={{ ...base, background: '#0f1611', color: '#c2f02f' }}
        aria-hidden
      >
        ★
      </span>
    );
  }
  if (conversation.kind === 'dynamic' && conversation.rule?.type === 'level') {
    // The level's own colour, like the level chip — «N4» reads at a glance.
    const lvl = getEloLevel(levelFloor(conversation.rule.level));
    return (
      <span
        className="flex items-center justify-center rounded-full flex-shrink-0 font-mono font-bold"
        style={{
          ...base,
          background: lvl.color,
          color: lvl.text,
          fontSize: Math.round(size * 0.3),
        }}
        aria-hidden
      >
        N{conversation.rule.level}
      </span>
    );
  }
  if (conversation.kind === 'group' || conversation.kind === 'dynamic') {
    return (
      <span
        className="flex items-center justify-center rounded-full flex-shrink-0"
        style={{ ...base, background: '#eef0ea', color: '#4a544c' }}
        aria-hidden
      >
        <Users size={Math.round(size * 0.42)} />
      </span>
    );
  }
  return (
    <span
      className="flex items-center justify-center rounded-full flex-shrink-0 font-display font-semibold"
      style={{ ...base, background: '#dfe3da', color: '#0f1611' }}
      aria-hidden
    >
      {initials(title)}
    </span>
  );
}

/** An ELO that sits inside the given level (for colour lookup only). */
function levelFloor(level: number): number {
  // getEloLevel maps ratings to levels 1–9; probe upward until the level matches.
  for (let elo = 0; elo <= 4000; elo += 25) if (getEloLevel(elo).level === level) return elo;
  return 0;
}
